import axios from "axios";
import { appUrl } from "../../../../../utils/config";

import {
  FETCH_NOTIFICATIONS_LOADING,
  FETCH_NOTIFICATIONS,
  FETCH_NOTIFICATIONS_FAIL,
  FETCH_NOTIFICATION_BY_ID,
  FETCH_NOTIFICATION_BY_ID_FAIL,
  MARK_NOTIFICATION_READ,
  MARK_NOTIFICATION_READ_FAIL,
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_ALL_NOTIFICATIONS_READ_FAIL,
  TOGGLE_NOTIFICATION_STAR,
  TOGGLE_NOTIFICATION_STAR_FAIL,
  FETCH_UNREAD_COUNT,
  CLEAR_NOTIFICATIONS_STATE,
  FETCH_PREFERENCE_EVENT_TYPES,
  FETCH_PREFERENCE_EVENT_TYPES_FAIL,
  FETCH_PREFERENCES_LOADING,
  FETCH_PREFERENCES,
  FETCH_PREFERENCES_FAIL,
  UPDATE_PREFERENCES,
  UPDATE_PREFERENCES_FAIL,
  FETCH_DISTRIBUTION_LOADING,
  FETCH_DISTRIBUTION,
  FETCH_DISTRIBUTION_FAIL,
  UPDATE_DISTRIBUTION,
  UPDATE_DISTRIBUTION_FAIL,
  UPLOAD_DISTRIBUTION,
  UPLOAD_DISTRIBUTION_FAIL,
  DOWNLOAD_DISTRIBUTION_LOADING,
  DOWNLOAD_DISTRIBUTION,
  DOWNLOAD_DISTRIBUTION_FAIL,
  CLEAR_DISTRIBUTION_STATE,
  FETCH_ORG_DEPT_ROLES,
  FETCH_ORG_DEPT_ROLES_FAIL,
  FETCH_CMO_DEPT_ROLES,
  FETCH_CMO_DEPT_ROLES_FAIL,
  FETCH_NOTIFICATION_CATEGORIES,
  FETCH_ROLES_BY_EVENT,
  FETCH_ROLES_BY_EVENT_LOADING,
  FETCH_ROLES_BY_EVENT_FAIL,
} from "../types";

// Base URL follows same pattern as all other Mosymphony endpoints
const getBase = () => `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

// ─── 1. Fetch notifications list ─────────────────────────────────────────────
// POST /fetchNotifications
// Body: { page, page_size, category?, is_read?, date_from?, date_to?, search?, sort_by?, sort_order? }
export let fetchNotificationsSource;
export const fetchNotifications = (params = {}) => (dispatch) => {
  dispatch({ type: FETCH_NOTIFICATIONS_LOADING });
  const CancelToken = axios.CancelToken;
  fetchNotificationsSource = CancelToken.source();

  axios
    .post(`${getBase()}/fetchNotifications`, params, {
      cancelToken: fetchNotificationsSource.token,
    })
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_NOTIFICATIONS, payload: res.data });
      } else {
        dispatch({ type: FETCH_NOTIFICATIONS_FAIL, payload: res.data });
      }
    })
    .catch((err) => {
      if (!axios.isCancel(err)) {
        dispatch({ type: FETCH_NOTIFICATIONS_FAIL, payload: err });
      }
    });
};

// ─── 2. Fetch single notification by ID (called on chevron expand) ───────────
// POST /fetchNotificationById
// Body: { id }  — id is user_event_id
// Response: { status: true, data: { ...Notification Object } }
export const fetchNotificationById = (data) => (dispatch) => {
  axios
    .post(`${getBase()}/fetchNotificationById`, data)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_NOTIFICATION_BY_ID, payload: res.data.data });
      } else {
        dispatch({ type: FETCH_NOTIFICATION_BY_ID_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_NOTIFICATION_BY_ID_FAIL, payload: err })
    );
};

// ─── 3. Fetch unread count (badge) ────────────────────────────────────────────
// POST /fetchUnreadCount  (no request body required)
// Response: { status: true, unreadCount: 7 }
export const fetchUnreadCount = () => (dispatch) => {
  axios
    .post(`${getBase()}/fetchUnreadCount`, {})
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_UNREAD_COUNT, payload: res.data.unreadCount });
      }
    })
    .catch((err) => console.error(err));
};

// ─── 3. Mark single notification as read ─────────────────────────────────────
// POST /markNotificationRead
// Body: { id }  — id is user_event_id
// Response: { status: true, message: "Notification marked as read" }
export const markNotificationRead = (data) => (dispatch) => {
  axios
    .post(`${getBase()}/markNotificationRead`, data)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: MARK_NOTIFICATION_READ, payload: { id: data.id } });
      } else {
        dispatch({ type: MARK_NOTIFICATION_READ_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: MARK_NOTIFICATION_READ_FAIL, payload: err })
    );
};

// ─── 4. Mark all notifications as read ───────────────────────────────────────
// POST /markAllNotificationsRead
// Body: { category? }  — omit category to mark all
// Response: { status: true, updated: 14 }
export const markAllNotificationsRead = (params = {}) => (dispatch) => {
  axios
    .post(`${getBase()}/markAllNotificationsRead`, params)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: MARK_ALL_NOTIFICATIONS_READ, payload: res.data });
      } else {
        dispatch({
          type: MARK_ALL_NOTIFICATIONS_READ_FAIL,
          payload: res.data,
        });
      }
    })
    .catch((err) =>
      dispatch({ type: MARK_ALL_NOTIFICATIONS_READ_FAIL, payload: err })
    );
};

// ─── 5. Toggle star ───────────────────────────────────────────────────────────
// POST /toggleNotificationStar
// Body: { id }  — id is user_event_id
// Response: { status: true, isStarred: true }
export const toggleNotificationStar = (data) => (dispatch) => {
  axios
    .post(`${getBase()}/toggleNotificationStar`, data)
    .then((res) => {
      if (res.data.status) {
        dispatch({
          type: TOGGLE_NOTIFICATION_STAR,
          payload: { id: data.id, isStarred: res.data.isStarred },
        });
      } else {
        dispatch({ type: TOGGLE_NOTIFICATION_STAR_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: TOGGLE_NOTIFICATION_STAR_FAIL, payload: err })
    );
};

// ─── 6. Clear state flags ─────────────────────────────────────────────────────
export const clearNotificationsState = () => ({
  type: CLEAR_NOTIFICATIONS_STATE,
});

// ─── 7. Fetch preference event types ──────────────────────────────────────────
// POST /fetchPreferenceEventTypes  (no body)
// Response: { status: true, data: [{ event_type_id, event_name, category }] }
export const fetchPreferenceEventTypes = () => (dispatch) => {
  axios
    .post(`${getBase()}/fetchPreferenceEventTypes`, {})
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_PREFERENCE_EVENT_TYPES, payload: res.data.data });
      } else {
        dispatch({ type: FETCH_PREFERENCE_EVENT_TYPES_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_PREFERENCE_EVENT_TYPES_FAIL, payload: err })
    );
};

// ─── 8. Fetch user notification preferences ───────────────────────────────────
// POST /fetchPreferences  (no body)
// Response: { status, data: { eventNotificationToUserMappingList: [{ objectType, eventNotifications }], ... } }
export const fetchPreferences = () => (dispatch) => {
  dispatch({ type: FETCH_PREFERENCES_LOADING });
  axios
    .post(`${getBase()}/fetchPreferences`, {})
    .then((res) => {
      if (res.data.status) {
        const inner = res.data.data || {};
        dispatch({
          type: FETCH_PREFERENCES,
          payload: inner.eventNotificationToUserMappingList || [],
        });
      } else {
        dispatch({ type: FETCH_PREFERENCES_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_PREFERENCES_FAIL, payload: err })
    );
};

// ─── 9. Update user notification preferences ──────────────────────────────────
// POST /updatePreferences
// Body: { mappings: [{ eventToUserMappingID, dashboardNotification, emailNotification }] }
// Response: { status: true }
export const updatePreferences = (mappings) => (dispatch) => {
  axios
    .post(`${getBase()}/updatePreferences`, { mappings })
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: UPDATE_PREFERENCES, payload: mappings });
      } else {
        dispatch({ type: UPDATE_PREFERENCES_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: UPDATE_PREFERENCES_FAIL, payload: err })
    );
};

// ─── 10. Fetch distribution list (admin) ──────────────────────────────────────
// GET /fetchDistribution
// Response: { status: true, data: [...] }
export const fetchDistribution = () => (dispatch) => {
  dispatch({ type: FETCH_DISTRIBUTION_LOADING });
  axios
    .get(`${getBase()}/fetchDistribution`)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_DISTRIBUTION, payload: res.data });
      } else {
        dispatch({ type: FETCH_DISTRIBUTION_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_DISTRIBUTION_FAIL, payload: err })
    );
};

// ─── 11. Update distribution roles for an event ──────────────────────────────
// POST /updateDistribution
// Body: { eventTypeID, brandUserAssociationReqd, CMOUserAssociationReqd, productTypeUserAssociationReqd,
//         roles: [{ eventToRoleMappingID?, collaboratorRoleID, departmentID, orgID, actionRequired, markForDelete }] }
// Response: { status: true }
export const updateDistribution = (data) => (dispatch) => {
  axios
    .post(`${getBase()}/updateDistribution`, data)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: UPDATE_DISTRIBUTION, payload: data });
      } else {
        dispatch({ type: UPDATE_DISTRIBUTION_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: UPDATE_DISTRIBUTION_FAIL, payload: err })
    );
};

// ─── 12. Upload distribution CSV ──────────────────────────────────────────────
// POST /uploadEventRoleMapping  (multipart/form-data, field: file)
// Response: { status: true }
export const uploadDistribution = (file, orgType) => (dispatch) => {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("orgType", orgType);
  axios
    .post(`${getBase()}/uploadEventRoleMapping`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => {
 
      if (res.data.status) {
        dispatch({ type: UPLOAD_DISTRIBUTION, payload: res.data });
      } else {
        dispatch({ type: UPLOAD_DISTRIBUTION_FAIL, payload: res.data });
      }
    })
    .catch((err) => {
      console.error("Upload API Error:", err);
      dispatch({ type: UPLOAD_DISTRIBUTION_FAIL, payload: err });
    });
};

// ─── 13. Download event notifications (Excel) ──────────────────────────────
// POST /downloadEventNotifications
// Response: { status, fileName, fileData: { type: "Buffer", data: [bytes] } }
export const downloadDistribution = (params = {}) => (dispatch) => {
  dispatch({ type: DOWNLOAD_DISTRIBUTION_LOADING });
  axios
    .post(`${getBase()}/downloadEventNotifications`, params)
    .then((res) => {
      if (res.data && res.data.status) {
        dispatch({ type: DOWNLOAD_DISTRIBUTION, payload: res.data });
      } else {
        dispatch({ type: DOWNLOAD_DISTRIBUTION_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: DOWNLOAD_DISTRIBUTION_FAIL, payload: err })
    );
};

// ─── 14. Clear distribution state flags ───────────────────────────────────────
export const clearDistributionState = () => ({
  type: CLEAR_DISTRIBUTION_STATE,
});

// ─── 15a. Fetch CMO departments + roles (via Corda fetchDepartmentAndRoles) ──
// POST /fetchCmoDeptRoles  body: { organization_id }
export const fetchCmoDeptRoles = (organizationId) => (dispatch) => {
  axios
    .post(`${getBase()}/fetchCmoDeptRoles`, { organization_id: organizationId })
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_CMO_DEPT_ROLES, payload: res.data.data });
      } else {
        dispatch({ type: FETCH_CMO_DEPT_ROLES_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_CMO_DEPT_ROLES_FAIL, payload: err })
    );
};

// ─── 15b. Fetch org / dept / roles for distribution edit popup ───────────────
// POST /fetchOrgDeptRoles  (optional body: { collaborator_type_id })
// Response: { status: true, data: { organizations, departments, roles } }
export const fetchOrgDeptRoles = (collaboratorTypeId) => (dispatch) => {
  const body = collaboratorTypeId ? { collaborator_type_id: collaboratorTypeId } : {};
  axios
    .post(`${getBase()}/fetchOrgDeptRoles`, body)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_ORG_DEPT_ROLES, payload: res.data.data });
      } else {
        dispatch({ type: FETCH_ORG_DEPT_ROLES_FAIL, payload: res.data });
      }
    })
    .catch((err) =>
      dispatch({ type: FETCH_ORG_DEPT_ROLES_FAIL, payload: err })
    );
};

// ─── 16. Fetch notification categories from DB ──────────────────────────────
// POST /fetchNotificationCategories
export const fetchNotificationCategories = () => (dispatch) => {
  axios
    .post(`${getBase()}/fetchNotificationCategories`)
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_NOTIFICATION_CATEGORIES, payload: res.data.data });
      }
    })
    .catch(() => {});
};

// ─── 17. Fetch roles for a specific event type (on view/edit click) ─────────
// POST /fetchRolesByEvent
export const fetchRolesByEvent = (eventTypeID) => (dispatch) => {
  dispatch({ type: FETCH_ROLES_BY_EVENT_LOADING });
  axios
    .post(`${getBase()}/fetchRolesByEvent`, { eventTypeID })
    .then((res) => {
      if (res.data.status) {
        dispatch({ type: FETCH_ROLES_BY_EVENT, payload: res.data.data });
      } else {
        dispatch({ type: FETCH_ROLES_BY_EVENT_FAIL, payload: res.data });
      }
    })
    .catch((err) => dispatch({ type: FETCH_ROLES_BY_EVENT_FAIL, payload: err }));
};
